import React, { useState, useEffect } from 'react';
import { ChevronUp, ChevronDown, Plus, Pencil, Trash2, X } from 'lucide-react';
import apiService from './services/api';
import { Panel, Badge, LoadingSpinner, Button } from './components';

function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    role: 'operator',
    phone_number: '',
    password: '',
    staff_id: null,
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [technicians, setTechnicians] = useState([]);

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.get('/auth/users/');
      setUsers(data.results || data);
    } catch (err) {
      setError(err.message || 'Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const getRoleVariant = (role) => {
    switch (role) {
      case 'admin': return 'info';
      case 'operator': return 'neutral';
      case 'technician': return 'success';
      default: return 'neutral';
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Get sort value
  const getSortValue = (user, field) => {
    switch (field) {
      case 'username':
        return user.username?.toLowerCase() || '';
      case 'email':
        return user.email?.toLowerCase() || '';
      case 'first_name':
        return user.first_name?.toLowerCase() || '';
      case 'last_name':
        return user.last_name?.toLowerCase() || '';
      case 'role':
        return user.role || '';
      case 'created_at':
        return user.created_at ? new Date(user.created_at).getTime() : 0;
      default:
        return '';
    }
  };

  // Sort users
  const sortedUsers = [...users].sort((a, b) => {
    const aVal = getSortValue(a, sortField);
    const bVal = getSortValue(b, sortField);

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Render sort icon
  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ChevronUp className="w-3 h-3 inline ml-1" />
    ) : (
      <ChevronDown className="w-3 h-3 inline ml-1" />
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Fetch technicians for dropdown
  const fetchTechnicians = async () => {
    try {
      const data = await apiService.get('/auth/technicians/');
      setTechnicians(data);
    } catch (err) {
      console.error('Error fetching technicians:', err);
    }
  };

  // Modal handlers
  const openCreateModal = async () => {
    setModalMode('create');
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      first_name: '',
      last_name: '',
      role: 'operator',
      phone_number: '',
      password: '',
      staff_id: null,
    });
    setFormError('');
    await fetchTechnicians();
    setShowModal(true);
  };

  const openEditModal = async (user) => {
    setModalMode('edit');
    setEditingUser(user);
    setFormData({
      username: user.username || '',
      email: user.email || '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      role: user.role || 'operator',
      phone_number: user.phone_number || '',
      password: '',
      staff_id: user.staff_id || null,
    });
    setFormError('');
    await fetchTechnicians();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormError('');
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'staff_id') {
      const staffId = value ? parseInt(value, 10) : null;
      setFormData(prev => ({ ...prev, staff_id: staffId }));

      // Auto-populate fields from selected technician (only in create mode or if fields are empty)
      if (staffId && modalMode === 'create') {
        const selectedTech = technicians.find(t => t.id === staffId);
        if (selectedTech) {
          setFormData(prev => ({
            ...prev,
            staff_id: staffId,
            first_name: prev.first_name || selectedTech.first_name || '',
            last_name: prev.last_name || selectedTech.last_name || '',
            email: prev.email || selectedTech.email || '',
            phone_number: prev.phone_number || selectedTech.phone_number || '',
            username: prev.username || selectedTech.email || '',
          }));
        }
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);

    try {
      const payload = { ...formData };

      // Remove empty password for edit mode
      if (modalMode === 'edit' && !payload.password) {
        delete payload.password;
      }

      // Only include staff_id for technician role
      if (payload.role !== 'technician') {
        payload.staff_id = null;
      }

      if (modalMode === 'create') {
        if (!payload.password) {
          setFormError('Password is required for new users');
          setSaving(false);
          return;
        }
        await apiService.post('/auth/users/', payload);
      } else {
        await apiService.patch(`/auth/users/${editingUser.id}/`, payload);
      }

      closeModal();
      fetchUsers();
    } catch (err) {
      setFormError(err.message || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  // Delete handlers
  const openDeleteConfirm = (user) => {
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const closeDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setUserToDelete(null);
  };

  const handleDelete = async () => {
    if (!userToDelete) return;

    setDeleting(true);
    try {
      await apiService.delete(`/auth/users/${userToDelete.id}/`);
      closeDeleteConfirm();
      fetchUsers();
    } catch (err) {
      alert(err.message || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const inputClass = "w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none text-sm";
  const labelClass = "block text-xs font-medium text-gray-700 mb-1";

  return (
    <div className="flex-1 bg-neutral-50 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      <div className="p-3">
        {/* Page Title */}
        <div className="mb-3 flex items-center justify-between">
          <h1 className="text-lg font-semibold text-gray-900 leading-none">Users</h1>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">{users.length} total</span>
            <Button size="sm" onClick={openCreateModal}>
              <Plus className="w-4 h-4 mr-1" />
              Add User
            </Button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <Panel className="p-6">
            <LoadingSpinner />
          </Panel>
        )}

        {/* Error State */}
        {error && (
          <Panel className="bg-red-50 border-red-200 mb-3">
            <p className="text-xs font-medium text-red-800">Error loading users</p>
            <p className="text-xs text-red-600 mt-0.5">{error}</p>
          </Panel>
        )}

        {/* Users Table */}
        {!loading && !error && (
          <Panel noPadding className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th
                      className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700 select-none"
                      onClick={() => handleSort('username')}
                    >
                      Username {renderSortIcon('username')}
                    </th>
                    <th
                      className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700 select-none"
                      onClick={() => handleSort('first_name')}
                    >
                      First Name {renderSortIcon('first_name')}
                    </th>
                    <th
                      className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700 select-none"
                      onClick={() => handleSort('last_name')}
                    >
                      Last Name {renderSortIcon('last_name')}
                    </th>
                    <th
                      className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700 select-none"
                      onClick={() => handleSort('email')}
                    >
                      Email {renderSortIcon('email')}
                    </th>
                    <th
                      className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700 select-none"
                      onClick={() => handleSort('role')}
                    >
                      Role {renderSortIcon('role')}
                    </th>
                    <th
                      className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700 select-none"
                      onClick={() => handleSort('created_at')}
                    >
                      Created {renderSortIcon('created_at')}
                    </th>
                    <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sortedUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-2 py-2 text-sm text-gray-900 font-medium">
                        {user.username}
                      </td>
                      <td className="px-2 py-2 text-sm text-gray-700">
                        {user.first_name || '-'}
                      </td>
                      <td className="px-2 py-2 text-sm text-gray-700">
                        {user.last_name || '-'}
                      </td>
                      <td className="px-2 py-2 text-sm text-gray-700">
                        {user.email || '-'}
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-1">
                          <Badge variant={getRoleVariant(user.role)}>
                            {user.role || 'operator'}
                          </Badge>
                          {user.role === 'technician' && user.staff_id && (
                            <span className="text-xs text-green-600" title={`Linked to Staff #${user.staff_id}`}>
                              (linked)
                            </span>
                          )}
                          {user.role === 'technician' && !user.staff_id && (
                            <span className="text-xs text-orange-500" title="Not linked to any team member">
                              (unlinked)
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-sm text-gray-500">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openEditModal(user)}
                            className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                            title="Edit user"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openDeleteConfirm(user)}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            title="Delete user"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {sortedUsers.length === 0 && (
                <div className="p-6 text-center text-gray-500 text-sm">
                  No users found
                </div>
              )}
            </div>
          </Panel>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {modalMode === 'create' ? 'Add New User' : 'Edit User'}
              </h2>
              <button
                onClick={closeModal}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-3">
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="first_name" className={labelClass}>First Name</label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleFormChange}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="last_name" className={labelClass}>Last Name</label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleFormChange}
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="username" className={labelClass}>Username *</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleFormChange}
                  className={inputClass}
                  required
                />
              </div>

              <div>
                <label htmlFor="email" className={labelClass}>Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  className={inputClass}
                  required
                />
              </div>

              <div>
                <label htmlFor="phone_number" className={labelClass}>Phone Number</label>
                <input
                  type="text"
                  id="phone_number"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleFormChange}
                  className={inputClass}
                  placeholder="+1234567890"
                />
              </div>

              <div>
                <label htmlFor="role" className={labelClass}>Role *</label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleFormChange}
                  className={inputClass}
                  required
                >
                  <option value="operator">Operator</option>
                  <option value="technician">Team Member</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              {formData.role === 'technician' && (
                <div>
                  <label htmlFor="staff_id" className={labelClass}>Link to Team Member</label>
                  <select
                    id="staff_id"
                    name="staff_id"
                    value={formData.staff_id || ''}
                    onChange={handleFormChange}
                    className={inputClass}
                  >
                    <option value="">-- Select a team member --</option>
                    {technicians.map((tech) => (
                      <option key={tech.id} value={tech.id}>
                        {tech.name}
                        {tech.linked_user_id && tech.linked_user_id !== editingUser?.id
                          ? ` (linked to ${tech.linked_username})`
                          : tech.linked_user_id === editingUser?.id
                            ? ' (current)'
                            : ''
                        }
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Link this user to an existing team member profile for appointments
                  </p>
                </div>
              )}

              <div>
                <label htmlFor="password" className={labelClass}>
                  Password {modalMode === 'create' ? '*' : '(leave blank to keep current)'}
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleFormChange}
                  className={inputClass}
                  minLength={modalMode === 'create' ? 8 : undefined}
                  required={modalMode === 'create'}
                  placeholder={modalMode === 'edit' ? 'Leave blank to keep current' : 'Min 8 characters'}
                />
              </div>

              <div className="flex justify-end gap-2 pt-3">
                <Button type="button" variant="secondary" onClick={closeModal}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? 'Saving...' : (modalMode === 'create' ? 'Create User' : 'Save Changes')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && userToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Delete User</h2>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete <strong>{userToDelete.username}</strong>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={closeDeleteConfirm}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UsersPage;
